<template>
	<UiModalMobile
		v-model="modelValue"
		class="cargo-details-modal"
		:is-need-btn-save="false"
		:is-need-cancel-btn="false"
		height="calc(100% - 32px)"
		min-height-body="unset"
		custom-padding="20px 16px"
	>
		<div class="cargo-details-modal__content">
			<div
				v-if="isLoading"
				class="cargo-details-modal__loader"
			>
				<UiLoader
					:radius="20"
					:circle-width="2"
					:color="'var(--primary)'"
				/>
			</div>
			<div
				v-else-if="details"
				class="cargo-details"
				@click="activePhoneContact = null"
			>
				<div class="cargo-details__header">
					<div class="cargo-details__title">
						<TypographyKTypographyBody2
							color="#E32F3E"
							font-type="bold"
						>
							Груз #{{ cargo.number }}
						</TypographyKTypographyBody2>
						<span
							v-if="details.deliveryToClient || details.deliveryFromTruck"
							class="cargo-details__delivery"
							:class="{
								'cargo-details__delivery--pickup': !details.deliveryToClient && details.deliveryFromTruck
							}"
						>
							{{ details.deliveryToClient ? 'Доставка с фуры' : 'Выдача с фуры' }}
						</span>
					</div>
					<div
						v-if="details?.paymentStatusName"
						class="cargo-details__payment"
						:style="{
							color: details?.paymentStatusBgColor || '#303733'
						}"
					>
						<Icon
							:name="details?.isPaid ? 'material-symbols:verified-rounded' : 'ix:certificate-exclamation-filled'"
							:color="details?.paymentStatusBgColor || '#303733'"
							size="23px"
						/>
						{{ details?.paymentStatusName || 'Статус оплаты не указан' }}
					</div>
				</div>

				<div class="cargo-details__clients">
					<div class="cargo-details__client-name cargo-details__sender">
						<strong>Отправитель:</strong>
						<span>{{ details?.clientSenderName || '—' }}</span>
					</div>
					<div class="cargo-details__client-name cargo-details__recipient">
						<strong>Получатель:</strong>
						<span>{{ details?.clientRecipientName || '—' }}</span>
					</div>
					<div class="cargo-details__client-info cargo-details__recipient-contact">
						<strong>К/л:</strong>
						<span>{{ details?.clientRecipientContactName || '—' }}</span>
					</div>
					<div class="cargo-details__client-info cargo-details__manager">
						<strong>Менеджер:</strong>
						<span>{{ details.managerName || '—' }}</span>
					</div>
					<div
						class="cargo-details__client-info cargo-details__recipient-phone"
						@click.stop
					>
						<strong>тел:</strong>
						<div class="cargo-details__phone-contact">
							<UiPopover
								:key="`${details.id}-recipient-phone`"
								position="auto"
								padding="0px"
								:offset-distance="'0'"
								:skid="'0'"
								@open="activePhoneContact = 'recipient'"
								@close="activePhoneContact = null"
							>
								<template #button>
									<button
										type="button"
										class="cargo-details__phone-trigger"
										:aria-expanded="activePhoneContact === 'recipient'"
									>
										{{ formatPhone(details?.clientRecipientContactPhoneMain || '') || '' }}
									</button>
								</template>
								<template #body="{ close }">
									<div class="cargo-details__phone-actions">
										<a
											:href="`tel:${details?.clientRecipientContactPhoneMain}`"
											@click="close"
										>
											<UiButtonIcon
												background="linear-gradient(180deg, #E3E3E3 0%, #9196A2 100%)"
												padding="3px"
											>
												<Icon
													name="garden:phone-call-speaker-fill-12"
													color="white"
												/>
											</UiButtonIcon>
											Позвонить
										</a>
										<a
											:href="getWhatsAppHref(details?.clientRecipientContactPhoneMain)"
											target="_blank"
											rel="noopener noreferrer"
											@click="close"
										>
											<UiButtonIcon
												background="linear-gradient(180deg, #8FEAB1 0%, #25D366 100%)"
												padding="3px"
											>
												<Icon
													name="ic:baseline-whatsapp"
													color="white"
												/>
											</UiButtonIcon>
											Написать в WhatsApp
										</a>
									</div>
								</template>
							</UiPopover>
						</div>
					</div>
					<div
						v-if="details?.managerPhone"
						class="cargo-details__client-info cargo-details__manager-phone"
						@click.stop
					>
						<strong>тел:</strong>
						<div class="cargo-details__phone-contact">
							<UiPopover
								:key="`${details.id}-manager-phone`"
								position="auto"
								padding="0px"
								:offset-distance="'0'"
								:skid="'0'"
								@open="activePhoneContact = 'manager'"
								@close="activePhoneContact = null"
							>
								<template #button>
									<button
										type="button"
										class="cargo-details__phone-trigger"
										:aria-expanded="activePhoneContact === 'manager'"
									>
										{{ formatPhone(details?.managerPhone) }}
									</button>
								</template>
								<template #body="{ close }">
									<div class="cargo-details__phone-actions">
										<a
											:href="getPhoneHref(details?.managerPhone)"
											@click="close"
										>
											<UiButtonIcon
												background="linear-gradient(180deg, #E3E3E3 0%, #9196A2 100%)"
												padding="3px"
											>
												<Icon
													name="garden:phone-call-speaker-fill-12"
													color="white"
												/>
											</UiButtonIcon>
											Позвонить
										</a>
										<a
											:href="getWhatsAppHref(details?.managerPhone)"
											target="_blank"
											rel="noopener noreferrer"
											@click="close"
										>
											<UiButtonIcon
												background="linear-gradient(180deg, #8FEAB1 0%, #25D366 100%)"
												padding="3px"
											>
												<Icon
													name="ic:baseline-whatsapp"
													color="white"
												/>
											</UiButtonIcon>
											Написать в WhatsApp
										</a>
									</div>
								</template>
							</UiPopover>
						</div>
					</div>
				</div>

				<div class="cargo-details__metrics">
					<div class="cargo-details__metric">
						<span>Вес, т</span>
						<strong>{{ details?.weight }}</strong>
					</div>
					<div class="cargo-details__metric">
						<span>Объём, м³</span>
						<strong>{{ details?.volume }}</strong>
					</div>
					<div class="cargo-details__metric">
						<span>Кол-во</span>
						<strong>{{ details?.qty }}</strong>
					</div>
					<div class="cargo-details__metric">
						<span>Габариты</span>
						<strong>{{ cargo.dimensions || '—' }}</strong>
					</div>
				</div>

				<section class="cargo-details__summary">
					<div><strong>Описание:</strong> {{ details?.description || details?.name || '—' }}</div>
					<div><strong>Комментарий:</strong> {{ details?.comment || '—' }}</div>
					<div><strong>Адрес:</strong> {{ details?.addressIn || cargo.addressIn || '—' }}</div>
				</section>

				<CommonCardWithItems
					v-model="cargoPropertyName"
					class="cargo-details__properties-card"
					label="Свойства груза"
					:readonly="true"
					:is-need-change-btn="false"
					:is-need-always-visible-input="false"
				>
					<template #opened>
						<div class="cargo-details__properties">
							<div
								v-for="property in cargoProperties"
								:key="property.key"
								class="cargo-details__property"
							>
								<UiCheckBox
									:is-checked-props="details?.[property.key]"
									:disable="true"
								>
									<div class="cargo-details__property-label">
										<div
											v-if="property.isPallet"
											class="cargo-details__pallet-icon"
										>
											<Icon
												name="fa7-solid:pallet"
												:color="property.color"
											/>
											<Icon
												name="fa7-solid:pallet"
												class="cargo-details__pallet-icon-bottom"
												:color="property.color"
											/>
										</div>
										<Icon
											v-else
											:name="property.icon"
											:color="property.color"
										/>
										<span>{{ property.label }}</span>
									</div>
								</UiCheckBox>
							</div>
						</div>
					</template>

					<template #closed>
						<div class="cargo-details__property-icons">
							<template
								v-for="property in cargoProperties.filter((item) => details[item.key])"
								:key="property.key"
							>
								<div
									v-if="property.isPallet"
									class="cargo-details__pallet-icon"
								>
									<Icon
										name="fa7-solid:pallet"
										:color="property.color"
									/>
									<Icon
										name="fa7-solid:pallet"
										class="cargo-details__pallet-icon-bottom"
										:color="property.color"
									/>
								</div>
								<Icon
									v-else
									:name="property.icon"
									:color="property.color"
								/>
							</template>
						</div>
					</template>
				</CommonCardWithItems>

				<section class="cargo-details__documents">
					<UiCheckBox
						v-for="document in documentFlags"
						:key="document.key"
						:is-checked-props="getDocumentFlag(document.key)"
						:disable="true"
					>
						<span>{{ document.label }}</span>
					</UiCheckBox>
				</section>

				<section
					class="cargo-details__delivery-comment"
					v-if="false"
				>
					<button
						type="button"
						class="cargo-details__section-toggle"
						:aria-expanded="isDeliveryCommentExpanded"
						@click="isDeliveryCommentExpanded = !isDeliveryCommentExpanded"
					>
						<span class="cargo-details__section-heading">
							<Icon
								name="fluent:comment-24-regular"
								color="#2e4a85"
							/>
							Комментарий по выдаче
						</span>
						<span class="cargo-details__expand">
							{{ isDeliveryCommentExpanded ? 'свернуть' : 'развернуть' }}
							<Icon
								name="material-symbols:keyboard-arrow-down-rounded"
								color="#2e4a85"
								:class="{ 'cargo-details__arrow--expanded': isDeliveryCommentExpanded }"
							/>
						</span>
					</button>
					<p :class="{ 'cargo-details__delivery-comment-text--collapsed': !isDeliveryCommentExpanded }">
						{{ details.deliveryComment || 'Комментарий по выдаче не указан.' }}
					</p>
				</section>

				<section class="cargo-details__comments">
					<div class="cargo-details__comments-header">
						<span class="cargo-details__section-heading">
							<Icon
								name="fluent:comment-24-regular"
								color="#171717"
							/>
							Все комментарии
						</span>
						<UiButton
							:color="ButtonType.primary"
							:size="ButtonSizeType.small"
							@click="isCommentModalOpen = true"
						>
							<Icon name="fluent:comment-add-20-regular" />
							Добавить
						</UiButton>
					</div>
					<div
						v-if="comments.length"
						class="cargo-details__comment-list"
					>
						<div
							v-for="comment in comments"
							:key="comment.id"
							class="cargo-details__comment"
						>
							<span class="cargo-details__comment-dot"></span>
							<div>
								<small>{{ getDateInLocaleString(comment.dtCreate) }}</small>
								<p>{{ comment.text }}</p>
							</div>
							<UiButtonIcon
								padding="2px"
								background="transparent"
								@click="deleteComment(comment)"
							>
								<Icon
									name="solar:trash-bin-trash-linear"
									color="#2E4A85"
									width="22px"
									height="22px"
								/>
							</UiButtonIcon>
						</div>
					</div>
					<p
						v-else
						class="cargo-details__empty-comments"
					>
						Комментариев пока нет
					</p>
				</section>
			</div>
			<div
				v-else
				class="cargo-details-modal__error"
			>
				<p>{{ error || 'Не удалось загрузить информацию о грузе' }}</p>
				<UiButton
					:color="ButtonType.primary"
					:size="ButtonSizeType.small"
					@click="emits('retry')"
				>
					Повторить загрузку
				</UiButton>
			</div>
		</div>
		<template #custom-actions>
			<div class="cargo-details-modal__actions">
				<UiButton
					:color="ButtonType.outline"
					:size="ButtonSizeType.big"
					@click="modelValue = false"
				>
					Отмена
				</UiButton>
				<UiButton
					v-if="(details?.deliveryFromTruck || details?.deliveryToClient) && !details?.hasDeliveried"
					:color="ButtonType.green"
					:size="ButtonSizeType.big"
					@click="emits('issue')"
				>
					Выдать груз
				</UiButton>
			</div>
		</template>
	</UiModalMobile>
	<PagesCargosAddedCommentModal
		v-if="details && isCommentModalOpen"
		v-model="isCommentModalOpen"
		:current-cargo="{ id: details.id }"
		:is-loading="cargoCommentStore.loading.addCargoComment"
		:z-index="10000"
		@save="saveComment"
	/>
	<UiModalConfirm
		v-if="isDeleteCommentModalOpen"
		v-model="isDeleteCommentModalOpen"
		title="Вы уверены, что хотите удалить комментарий?"
		:is-need-save-btn="true"
		@save="confirmDeleteComment"
	/>
</template>

<script setup lang="ts">
	import { getDateInLocaleString } from '~/helpers/date';
	import { useCargoCommentStore } from '~/store/cargoComment.store';
	import type { ICargoCommentItem, ICargoCommentAddResponse } from '~~/shared/interfaces/cargoComment';
	import type { ICargoByTripItem, ICargoInTripDetails } from '~~/shared/interfaces/truck';
	import { ButtonSizeType, ButtonType } from '~~/shared/interfaces/ui';

	const props = defineProps<{
		cargo: ICargoByTripItem;
		details: ICargoInTripDetails | null;
		isLoading: boolean;
		error: string;
	}>();

	const emits = defineEmits<{
		issue: [];
		retry: [];
	}>();
	const modelValue = defineModel<boolean>({ required: true });
	const cargoCommentStore = useCargoCommentStore();
	const cargoPropertyName = ref('');
	const isDeliveryCommentExpanded = ref(false);
	const isCommentModalOpen = ref(false);
	const isDeleteCommentModalOpen = ref(false);
	const activePhoneContact = ref<'recipient' | 'manager' | null>(null);
	const comments = ref<ICargoCommentItem[]>([]);
	const currentComment = ref<ICargoCommentItem | null>(null);

	const documentFlags: {
		key: 'documentAreMissing' | 'allDocuments' | 'naklOnly' | 'cmrOnly';
		label: string;
	}[] = [
		{ key: 'documentAreMissing', label: 'Полное отсутствие документов' },
		{ key: 'allDocuments', label: 'Есть все документы' },
		{ key: 'naklOnly', label: 'Только накладные' },
		{ key: 'cmrOnly', label: 'Только CRM' }
	];

	const formatPhone = (phone: string) => {
		const digits = phone?.replace(/\D/g, '');
		if (digits.length === 11) {
			return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
		}
		return phone;
	};

	const getPhoneHref = (phone?: string | null) => {
		const normalizedPhone = phone?.trim();
		if (!normalizedPhone) return '';
		return `tel:${normalizedPhone.startsWith('+') ? normalizedPhone : `+${normalizedPhone}`}`;
	};

	const getWhatsAppHref = (phone: string) => `https://wa.me/${phone?.replace(/\D/g, '')}`;

	const getDocumentFlag = (key: (typeof documentFlags)[number]['key']) => {
		if (key === 'allDocuments') {
			return props.details?.allDocuments ?? props.details?.hasDocuments ?? false;
		}
		return props.details?.[key] ?? false;
	};

	watch(
		() => props.details?.id,
		() => {
			isDeliveryCommentExpanded.value = false;
			activePhoneContact.value = null;
		}
	);

	const saveComment = async (payload: ICargoCommentAddResponse) => {
		const response = await cargoCommentStore.addCargoComment(payload);
		if (response.status === 'success') {
			await getComments();
			isCommentModalOpen.value = false;
		}
	};

	const deleteComment = (comment: ICargoCommentItem) => {
		currentComment.value = comment;
		isDeleteCommentModalOpen.value = true;
	};

	const confirmDeleteComment = async () => {
		if (!currentComment.value) return;
		const response = await cargoCommentStore.cargoCommentDelete({ id: currentComment.value.id });
		if (response.status === 'success') {
			comments.value = comments.value.filter((comment) => comment.id !== currentComment.value?.id);
			isDeleteCommentModalOpen.value = false;
			currentComment.value = null;
		}
	};

	const getComments = async () => {
		const cargoId = props.cargo.id;
		if (!cargoId) return;
		const response = await cargoCommentStore.getCargoComments({ cargoId });
		if (cargoId !== props.cargo.id) return;
		if (response.status === 'success') {
			comments.value = response.data || [];
		}
	};

	watch(
		() => props.cargo.id,
		() => {
			comments.value = [];
			void getComments();
		},
		{ immediate: true }
	);

	const cargoProperties: {
		key: 'hasPallets' | 'hasDocuments' | 'isFragile' | 'isOversized' | 'hasSnt' | 'hasFito';
		label: string;
		icon: string;
		color: string;
		isPallet?: boolean;
	}[] = [
		{
			key: 'hasPallets',
			label: 'Есть паллеты',
			icon: 'fa7-solid:pallet',
			color: '#af8100',
			isPallet: true
		},
		{
			key: 'hasDocuments',
			label: 'Есть документы',
			icon: 'fluent:document-checkmark-20-regular',
			color: '#14ab87'
		},
		{
			key: 'isFragile',
			label: 'Хрупкий',
			icon: 'streamline:shipping-box-fragile-fragile-shipping-glass-delivery-wine-crack-shipment-sign-sticker',
			color: '#e32f3e'
		},
		{
			key: 'isOversized',
			label: 'Негабарит',
			icon: 'streamline:shipping-transfer-cart-package-box-fulfillment-cart-warehouse-shipping-delivery',
			color: '#af8100'
		},
		{
			key: 'hasSnt',
			label: 'Поданы СНТ',
			icon: 'fluent:document-bullet-list-16-regular',
			color: '#006ea9'
		},
		{
			key: 'hasFito',
			label: 'Выписано ФИТО',
			icon: 'lucide:leaf',
			color: '#1db12a'
		}
	];
</script>

<style scoped lang="scss">
	.cargo-details-modal__actions {
		display: flex;
		gap: 10px;
		padding: 0 4px env(safe-area-inset-bottom);

		.ui-button {
			width: 100%;
		}

		@media (max-width: $extra-sm) {
			flex-direction: column;
		}
	}

	.cargo-details-modal__content {
		min-height: 100%;
	}

	.cargo-details-modal__loader,
	.cargo-details-modal__error {
		display: flex;
		min-height: 240px;
		align-items: center;
		justify-content: center;
		text-align: center;
	}

	.cargo-details-modal__error {
		flex-direction: column;
		gap: 12px;

		p {
			margin: 0;
		}
	}

	.cargo-details {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 8px 0 12px;
		color: #303733;
		font-size: 12px;

		&__header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 6px;
		}

		&__title {
			display: flex;
			min-width: 0;
			flex-wrap: wrap;
			align-items: center;
			gap: 6px;
		}

		&__delivery {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			border-radius: 14px;
			padding: 3px 4px;
			color: #000000;
			font-size: 10px;
			white-space: nowrap;
			background-color: #d8f2ef;

			&::before {
				width: 11px;
				height: 11px;
				border-radius: 50%;
				background-color: #3cb3ad;
				content: '';
			}

			&--pickup {
				background-color: #dceaff;

				&::before {
					background-color: #4180ff;
				}
			}
		}

		&__payment {
			display: flex;
			flex: 0 0 auto;
			flex-direction: column;
			align-items: center;
			gap: 1px;
			font-size: 10px;
			text-align: center;
		}

		&__clients {
			display: grid;
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
			grid-template-areas:
				'sender .'
				'recipient .'
				'recipient-contact manager'
				'recipient-phone manager-phone';
			gap: 2px 8px;
			line-height: 1.35;
		}

		&__client-name {
			min-width: 0;
			font-size: 14px;
			line-height: 1;

			strong {
				font-weight: 700;
				margin-right: 3px;
			}

			span {
				font-weight: 400;
			}
		}

		&__sender {
			grid-area: sender;
			span {
				color: #1c3363;
			}
		}

		&__recipient {
			grid-area: recipient;
		}

		&__recipient-contact {
			grid-area: recipient-contact;
		}

		&__manager {
			grid-area: manager;
		}

		&__recipient-phone {
			grid-area: recipient-phone;
		}

		&__manager-phone {
			grid-area: manager-phone;
		}

		&__client-info {
			min-width: 0;
			display: flex;
			flex-wrap: wrap;
			align-items: baseline;
			gap: 3px;
			font-size: 12px;
			line-height: 1;

			strong {
				flex: 0 0 auto;
				font-weight: 700;
			}

			> span {
				min-width: 0;
				font-weight: 400;
			}
		}

		&__phone-contact {
			width: max-content;
			max-width: 100%;
		}

		&__phone-trigger {
			border: 0;
			padding: 0;
			color: #2e65a1;
			font: inherit;
			text-align: left;
			text-decoration: underline dotted;
			background: transparent;
		}

		&__phone-actions {
			display: grid;
			min-width: 170px;
			max-width: calc(100vw - 40px);
			gap: 4px;
			border-radius: 8px;
			padding: 5px;
			background: #fff;
			box-shadow: 0 2px 10px rgb(28 38 64 / 18%);

			a {
				display: flex;
				min-width: 0;
				align-items: center;
				gap: 7px;
				border-radius: 5px;
				padding: 5px 6px;
				color: #303733;
				font-size: 11px;
				text-decoration: none;
				overflow-wrap: anywhere;
				white-space: normal;

				&:hover {
					background-color: #f1f3f8;
				}
			}
		}

		&__metrics {
			display: grid;
			grid-template-columns: repeat(4, minmax(0, 1fr));
			gap: 11px;
		}

		&__metric {
			display: flex;
			min-width: 0;
			min-height: 35px;
			flex-direction: column;
			gap: 2px;
			border-radius: 7px;
			padding: 5px 7px;
			background-color: #e9ecf4;
			margin-top: 0px;

			span {
				overflow: hidden;
				color: #757484;
				font-size: 12px;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			strong {
				overflow: hidden;
				font-size: 12px;
				font-weight: 600;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}

		&__summary {
			display: flex;
			flex-direction: column;
			gap: 2px;
			border-radius: 12px;
			padding: 11px 13px;
			background-color: #e9ecf4;
			line-height: 1.35;

			strong {
				font-weight: 700;
			}
		}

		&__section,
		&__documents,
		&__comments {
			border-radius: 12px;
			padding: 10px 12px;
			background-color: #e9ecf4;
		}

		&__properties-card {
			:deep(.input-wrapper) {
				border-radius: 12px;
				padding: 10px 12px;
				background-color: #e9ecf4;
			}

			:deep(.content-header) {
				align-items: center;
			}

			:deep(.content-header .label) {
				color: #171717 !important;
				font-size: 12px;
			}

			:deep(.content-header__btn) {
				color: #2e4a85;
				font-size: 10px;
				text-transform: lowercase;
			}

			:deep(.label__content) {
				gap: 8px;
			}
		}

		&__section-toggle {
			display: flex;
			width: 100%;
			align-items: center;
			justify-content: space-between;
			gap: 8px;
			border: 0;
			padding: 0;
			color: #171717;
			font-size: 12px;
			text-align: left;
			background: transparent;
		}

		&__section-heading,
		&__expand {
			display: inline-flex;
			align-items: center;
			gap: 5px;
		}

		&__section-heading {
			:deep(svg) {
				width: 18px;
				height: 18px;
				flex: 0 0 18px;
			}
		}

		&__expand {
			color: #2e4a85;
			font-size: 12px;
			white-space: nowrap;
			text-decoration: underline dotted;
		}

		&__arrow--expanded {
			transform: rotate(180deg);
		}

		&__property-icons {
			display: flex;
			min-height: 20px;
			align-items: center;
			gap: 10px;

			:deep(svg) {
				width: 20px;
				height: 20px;
			}
		}

		&__properties {
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 9px 8px;
			padding-top: 2px;
		}

		&__property {
			display: flex;
			min-width: 0;
			align-items: center;
			font-size: 11px;

			:deep(svg) {
				width: 18px;
				height: 18px;
				flex: 0 0 18px;
			}

			:deep(.ui-checkbox) {
				pointer-events: none;
			}

			:deep(.q-checkbox__label) {
				width: 100%;
			}
		}

		&__property-label {
			display: flex;
			align-items: center;
			gap: 6px;
			color: #303733;
			font-size: 11px;
		}

		&__pallet-icon {
			display: flex;
			flex-direction: column;
			margin-top: -9px;

			:deep(.cargo-details__pallet-icon-bottom) {
				margin-top: -12px;
			}
		}

		&__properties-card &__property-icons {
			padding: 0;
		}

		&__documents {
			display: flex;
			flex-direction: column;
			gap: 8px;
			padding: 11px 14px;

			:deep(.ui-checkbox) {
				pointer-events: none;
			}

			:deep(.q-checkbox__label) {
				font-size: 11px;
			}
		}

		&__delivery-comment {
			border-radius: 12px;
			padding: 10px 12px;
			background-color: #ffecd6;

			p {
				margin: 8px 0 0;
				border-radius: 10px;
				padding: 10px;
				color: #757484;
				font-size: 11px;
				line-height: 1.35;
				background-color: var(--white);
			}
		}

		&__delivery-comment-text--collapsed {
			display: -webkit-box;
			overflow: hidden;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
		}

		&__comments {
			padding: 9px 10px;
		}

		&__comments-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 8px;

			:deep(button) {
				min-height: 30px;
				padding-right: 10px;
				padding-left: 10px;
			}
		}

		&__comment-list {
			display: flex;
			flex-direction: column;
			gap: 8px;
			padding-top: 10px;
		}

		&__comment {
			display: grid;
			grid-template-columns: 10px minmax(0, 1fr) 24px;
			align-items: start;
			gap: 6px;
			padding-left: 2px;

			small {
				color: #9395a6;
				font-size: 9px;
			}

			p {
				margin: 1px 0 0;
				color: #656a76;
				font-size: 10px;
				line-height: 1.3;
			}
		}

		&__comment-dot {
			width: 9px;
			height: 9px;
			margin-top: 4px;
			border: 1px solid #a5a7b4;
			border-radius: 50%;
			background-color: #fff;
		}

		&__empty-comments {
			margin: 8px 0 0;
			color: #757484;
			font-size: 10px;
		}
	}
</style>
